import React, { useMemo, useState } from "react"
import { Box, Paper, Stack, Typography } from "@mui/material"
import SideBarLayout from "../../../layout/side-bar-layout"
import { TITLE_DESIGN_SYSTEM_PAGE } from "../../../../utils/constants"
import CodeViewer from "../../../components/CodeViewer/code-viewer"
import { renderGrid } from "../../../components/FieldRenderer"

const FieldRendererDesignSystemPage = () => {
    const TITLE_PAGE = `${TITLE_DESIGN_SYSTEM_PAGE} | Field Renderer`

    const companyOptions = useMemo(
        () => [
            { label: "BTG Pactual Pharma Participações S.A.", value: "13068817000143", cpf_cnpj: "13.068.817/0001-43" },
            { label: "XP Investimentos S.A.", value: "02421421000106", cpf_cnpj: "02.421.421/0001-06" },
            { label: "ACME Serviços Financeiros", value: "11222333000144", cpf_cnpj: "11.222.333/0001-44" },
        ],
        []
    )

    const fields = useMemo(
        () => [
            { key: "dividerDados", type: "divider", label: "Dados gerais", md: 12, breakAfter: true },
            { key: "razaoSocial", label: "Razão Social", maxLength: 80, md: 6 },
            { key: "documento", label: "CPF/CNPJ", maskType: "cpfCnpj", md: 6 },
            {
                key: "tipo",
                label: "Tipo de cadastro",
                type: "select",
                options: [
                    { value: "pessoa_juridica", label: "Pessoa Jurídica" },
                    { value: "pessoa_fisica", label: "Pessoa Física" },
                ],
                spacing: { mt: 1.5, ml: 0 },
                md: 6,
            },
            {
                key: "risco",
                label: "Risco",
                type: "radio",
                items: [
                    { value: "baixo", label: "Baixo" },
                    { value: "medio", label: "Médio" },
                    { value: "alto", label: "Alto" },
                ],
                defaultValue: "medio",
                md: 6,
            },
            {
                key: "empresa",
                label: "Empresa (autocomplete)",
                type: "autoComplete",
                options: companyOptions,
                filterKeys: ["label", "cpf_cnpj", "value"],
                md: 6,
            },
            {
                key: "observacoes",
                label: "Observações",
                type: "dynamicTextarea",
                minRows: 2,
                maxRows: 6,
                md: 12,
                spacing: { mt: 1.5, ml: 0 },
            },
        ],
        [companyOptions]
    )

    const [formState, setFormState] = useState({})
    const [fieldErrors, setFieldErrors] = useState({})

    const getFieldHandler = (key) => (value) => {
        setFormState((prev) => ({ ...prev, [key]: value }))
    }

    const validate = () => {
        const requiredKeys = ["razaoSocial", "documento", "tipo"]
        const nextErrors = {}
        requiredKeys.forEach((key) => {
            if (!formState[key]) {
                nextErrors[key] = { error: true, helperText: "Campo obrigatório" }
            }
        })
        setFieldErrors(nextErrors)
    }

    const codeUsage = `import FieldRenderer, { renderGrid } from "src/v2/components/FieldRenderer"

const fields = [
  { key: "razaoSocial", label: "Razão Social", md: 6 },
  { key: "documento", label: "CPF/CNPJ", maskType: "cpfCnpj", md: 6 },
  { key: "tipo", label: "Tipo", type: "select", options: [
      { value: "pessoa_juridica", label: "Pessoa Jurídica" },
      { value: "pessoa_fisica", label: "Pessoa Física" },
    ], md: 6 },
  { key: "risco", label: "Risco", type: "radio", items: [
      { value: "baixo", label: "Baixo" },
      { value: "medio", label: "Médio" },
      { value: "alto", label: "Alto" },
    ], defaultValue: "medio", md: 6 },
  { key: "empresa", label: "Empresa", type: "autoComplete", options: companyOptions, md: 6 },
  { key: "observacoes", label: "Observações", type: "dynamicTextarea", minRows: 2, maxRows: 6, md: 12 },
]

const [formState, setFormState] = useState({})
const [fieldErrors, setFieldErrors] = useState({})

const getFieldHandler = (key) => (value) => setFormState((prev) => ({ ...prev, [key]: value }))

const validate = () => {
  const required = ["razaoSocial", "documento", "tipo"]
  const nextErrors = {}
  required.forEach((key) => {
    if (!formState[key]) nextErrors[key] = { error: true, helperText: "Campo obrigatório" }
  })
  setFieldErrors(nextErrors)
}

return (
  <Box>
    {renderGrid(fields, formState, getFieldHandler, fieldErrors)}
    <Button onClick={validate}>Validar</Button>
  </Box>
)`

    const codeFieldProps = `type FieldConfig = {
  key: string
  label: string
  type?: "select" | "radio" | "checkbox" | "autoComplete" | "dynamicTextarea" | "divider" | "line"
  options?: { value: string | number; label: string }[]   // select
  items?: { value: string; label: string }[]              // radio
  maxLength?: number
  mask?: string
  maskType?: string
  helperText?: string
  md?: number
  spacing?: number | { mt?: number; mb?: number; ml?: number; mr?: number }
  filterKeys?: string[]                                   // autocomplete
  minRows?: number                                        // textarea
  maxRows?: number
  breakAfter?: boolean
  defaultValue?: string | number | boolean
}`

    return (
        <SideBarLayout
            header
            title={TITLE_PAGE}
            headerTitle={TITLE_PAGE}
        >
            <Box sx={{ mt: -2 }}>
                <Stack spacing={3}>
                    <Paper>
                        <Box p={3}>
                            <Typography variant="h4" gutterBottom color="common.white">
                                {`FieldRenderer`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {`Abstração usada no Cadastro Manual/OCR para renderizar campos heterogêneos a partir de um schema. Permite combinar textos, máscaras, selects, rádios, autocomplete e textarea dinâmica com regras de espaçamento e validação.`}
                            </Typography>

                            <Box mt={3}>
                                <Typography variant="h6" gutterBottom color="common.white">
                                    {`Exemplo principal`}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" paragraph>
                                    {`Campos controlados via state local. O autocomplete funciona apenas com options locais; em casos avançados use um controller externo.`}
                                </Typography>

                                <Box sx={{ backgroundColor: "#14192b", p: 2, borderRadius: 1 }}>
                                    {renderGrid(fields, formState, getFieldHandler, fieldErrors, { rowSpacing: 1.5 })}
                                </Box>

                                <Box mt={2}>
                                    <Typography variant="body2" color="text.secondary">
                                        <strong>Estado atual:</strong> {JSON.stringify(formState, null, 0)}
                                    </Typography>
                                </Box>

                                <Box mt={2}>
                                    <Typography
                                        variant="body2"
                                        color="primary.light"
                                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                                        onClick={validate}
                                    >
                                        {`Rodar validação simples`}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>

                    <Paper>
                        <Box p={3}>
                            <Typography variant="h6" gutterBottom color="common.white">
                                {`Uso básico com renderGrid`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                {`O helper renderGrid calcula quebras de linha automaticamente com base no md de cada campo e propaga os handlers/onChange/erros para cada FieldRenderer.`}
                            </Typography>

                            <Paper variant="outlined">
                                <Box p={2}>
                                    <CodeViewer code={codeUsage} language="jsx" />
                                </Box>
                            </Paper>
                        </Box>
                    </Paper>

                    <Paper>
                        <Box p={3}>
                            <Typography variant="h6" gutterBottom color="common.white">
                                {`Principais propriedades do schema`}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                {`Configurações aceitas no FieldRenderer para cada tipo de campo.`}
                            </Typography>

                            <Paper variant="outlined">
                                <Box p={2}>
                                    <Typography variant="subtitle2" gutterBottom color="common.white">
                                        {`Tipo das props (exemplo em TypeScript`}
                                        {`)`}
                                    </Typography>
                                    <CodeViewer code={codeFieldProps} language="tsx" />
                                </Box>
                            </Paper>
                        </Box>
                    </Paper>
                </Stack>
            </Box>
        </SideBarLayout>
    )
}

export default FieldRendererDesignSystemPage
